import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { OBSERVATIONS, DOMAINS } from "@/data/content";
import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";

const AUTOPLAY_MS = 8000;

export const ObservationCarousel = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = OBSERVATIONS.length;
  const timer = useRef(null);

  const next = useCallback(() => setIndex((i) => (i + 1) % total), [total]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + total) % total), [total]);

  useEffect(() => {
    if (paused) return;
    timer.current = window.setInterval(next, AUTOPLAY_MS);
    return () => window.clearInterval(timer.current);
  }, [paused, next]);

  // Pause when off-screen / not visible (respects user attention).
  useEffect(() => {
    const onVisibility = () => setPaused(document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  // Keyboard nav
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  const obs = OBSERVATIONS[index];
  const domain = DOMAINS.find((d) => d.slug === obs.domain);

  return (
    <section
      data-testid="home-observation-carousel"
      className="relative bg-ink-base py-24 md:py-36 border-t border-ink-line"
      aria-roledescription="carousel"
      aria-label="Current observations"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="container-edge">
        <div className="flex items-end justify-between gap-6 mb-12 md:mb-16">
          <div>
            <p className="eyebrow-gold mb-4">Current Observations · Cornerstones</p>
            <h2 className="display-serif text-3xl md:text-5xl text-paper-primary text-balance">
              Eleven things that keep being true.
            </h2>
          </div>
          <div className="hidden md:block text-right">
            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-paper-tertiary">
              {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
            </p>
            <p className="eyebrow mt-2">{domain?.name}</p>
          </div>
        </div>

        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-9">
            <div
              role="group"
              aria-roledescription="slide"
              aria-label={`Observation ${index + 1} of ${total}`}
              className="min-h-[220px] md:min-h-[260px]"
              key={obs.slug}
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-paper-mute mb-6">
                Observation No. {String(index + 1).padStart(2, "0")} · {domain?.name}
              </p>
              <p className="display-serif text-3xl md:text-5xl lg:text-6xl text-paper-primary leading-[1.05] tracking-[-0.015em] text-balance animate-fade-up">
                {obs.headline}
              </p>
              <p className="mt-6 max-w-3xl text-paper-secondary text-lg md:text-2xl font-serif leading-relaxed text-pretty animate-fade-up">
                {obs.body}
              </p>
              {obs.note && (
                <p className="mt-6 max-w-2xl text-paper-tertiary text-sm md:text-base italic font-serif leading-relaxed">
                  {obs.note}
                </p>
              )}
            </div>

            <div className="hairline mt-14" />

            <div className="flex items-center justify-between gap-4 mt-6">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  data-testid="carousel-prev"
                  onClick={prev}
                  aria-label="Previous observation"
                  className="w-10 h-10 inline-flex items-center justify-center border border-ink-line text-paper-secondary hover:text-paper-primary hover:border-gold/50 transition-all duration-300"
                >
                  <ChevronLeft size={16} strokeWidth={1.5} />
                </button>
                <button
                  type="button"
                  data-testid="carousel-next"
                  onClick={next}
                  aria-label="Next observation"
                  className="w-10 h-10 inline-flex items-center justify-center border border-ink-line text-paper-secondary hover:text-paper-primary hover:border-gold/50 transition-all duration-300"
                >
                  <ChevronRight size={16} strokeWidth={1.5} />
                </button>
                <button
                  type="button"
                  data-testid="carousel-pause"
                  onClick={() => setPaused((p) => !p)}
                  aria-label={paused ? "Resume autoplay" : "Pause autoplay"}
                  className="ml-2 w-10 h-10 inline-flex items-center justify-center border border-ink-line text-paper-secondary hover:text-paper-primary hover:border-gold/50 transition-all duration-300"
                >
                  {paused ? <Play size={14} strokeWidth={1.5} /> : <Pause size={14} strokeWidth={1.5} />}
                </button>
              </div>

              <Link to="/thinking" className="btn-arrow" data-testid="carousel-to-thinking">
                <span>Read the archive</span>
                <span className="arrow">→</span>
              </Link>
            </div>
          </div>

          {/* Indicator column */}
          <div className="lg:col-span-3 hidden lg:block">
            <p className="eyebrow mb-6">Index</p>
            <ol className="space-y-2 max-h-[420px] overflow-auto pr-2">
              {OBSERVATIONS.map((o, i) => {
                const d = DOMAINS.find((x) => x.slug === o.domain);
                const active = i === index;
                return (
                  <li key={o.slug}>
                    <button
                      type="button"
                      onClick={() => setIndex(i)}
                      data-testid={`carousel-jump-${i}`}
                      className={`group w-full text-left flex items-baseline gap-3 py-2 border-b border-ink-line/60 transition-colors ${
                        active ? "text-gold-warm" : "text-paper-secondary hover:text-paper-primary"
                      }`}
                    >
                      <span className="font-mono text-[10px] tracking-[0.2em] text-paper-mute">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-sans text-xs uppercase tracking-[0.18em]">{d?.name}</span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ObservationCarousel;
